import { useEffect, useMemo, useState } from "react";
import { ITimezoneOption } from "react-timezone-select";

interface CountdownProps {
  created_at: string; // wall-clock time in `timezone`, e.g. "2026-10-26 09:49:08"
  durationMinutes: number;
  timezone: ITimezoneOption; // carries `.value` (IANA name) and optionally `.offset`
}

/**
 * Convert a wall-clock date string ("YYYY-MM-DD HH:mm:ss") that is expressed
 * in the given IANA timezone into a true UTC epoch (ms).
 *
 * Uses Intl so DST is handled correctly regardless of what the caller's
 * `timezone.offset` snapshot says.
 */
function zonedWallClockToUtc(dateStr: string, ianaZone: string): number {
  // Normalise: allow "T" or space as separator, tolerate trailing "Z".
  const cleaned = dateStr.trim().replace("T", " ").replace(/Z$/, "");

  const m = cleaned.match(
    /^(\d{4})-(\d{2})-(\d{2})[ ](\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (!m) {
    throw new Error(`Unparseable created_at: "${dateStr}"`);
  }

  const [, y, mo, d, h, mi, s = "0"] = m;

  // First guess: pretend the wall clock is UTC.
  let utcGuess = Date.UTC(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s),
  );

  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: ianaZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  // Iterate a couple of times to converge (one pass is usually enough).
  for (let i = 0; i < 2; i++) {
    const parts = fmt
      .formatToParts(new Date(utcGuess))
      .reduce<Record<string, string>>((acc, p) => {
        if (p.type !== "literal") acc[p.type] = p.value;
        return acc;
      }, {});

    const asUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour) % 24, // "24" can appear for midnight in some locales
      Number(parts.minute),
      Number(parts.second),
    );

    const diff = asUtc - utcGuess; // how far the zone is from our assumption
    if (diff === 0) break;
    utcGuess -= diff;
  }

  return utcGuess;
}

function formatTimeLeft(ms: number): string {
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

const Countdown: React.FC<CountdownProps> = ({
  created_at,
  durationMinutes,
  timezone,
}) => {
  // Compute start/end once per (created_at, duration, zone) — not on every tick.
  const { startTimeUtc, endTimeUtc, error } = useMemo(() => {
    try {
      const zone = timezone?.value || "UTC";
      const start = zonedWallClockToUtc(created_at, zone);
      const end = start + durationMinutes * 60_000;
      return {
        startTimeUtc: start,
        endTimeUtc: end,
        error: null as null | string,
      };
    } catch (e) {
      return {
        startTimeUtc: NaN,
        endTimeUtc: NaN,
        error: e instanceof Error ? e.message : "Invalid date",
      };
    }
  }, [created_at, durationMinutes, timezone?.value]);

  const getRemaining = () =>
    Number.isFinite(endTimeUtc) ? Math.max(endTimeUtc - Date.now(), 0) : 0;

  const [timeLeft, setTimeLeft] = useState<number>(() => getRemaining());

  useEffect(() => {
    if (!Number.isFinite(endTimeUtc)) return;

    setTimeLeft(getRemaining());
    const id = setInterval(() => setTimeLeft(getRemaining()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTimeUtc]);

  if (error) {
    return <div title={error}>00:00</div>;
  }

  if (timeLeft <= 0) return <div>00:00</div>;

  return <div>{formatTimeLeft(timeLeft)}</div>;
};

export default Countdown;
